import React, { useCallback, useEffect, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  FieldErrors,
  UseFormSetValue,
} from 'react-hook-form';
import { useShallow } from 'zustand/react/shallow';
import { VirtualizedFormSelect, Alert } from '@roger/ui-components';
import { AlertCircle } from 'lucide-react';
import { DateField } from '../../components/FormFields';
import { ClinicianSelect } from '../../components/clinician/ClinicianSelect';
import type { AvailableClinicianResponse } from '../../services/api/types.gen';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import { SmartSchedulingSuggestions } from './SmartSchedulingSuggestions';
import { DISCIPLINE_TYPE_OPTIONS } from '../../components/intake/schedulingVisitType';
import { useStore } from '../../store';
import { FeatureFlag } from '../../constants/featureFlags';
import { IntakeValidationErrorList } from '../../components/intake/IntakeValidationErrorList';
import { formErrorsToValidationItems } from '../../components/intake/intakeValidationErrorItems';

interface RogerSchedulingStepFormContentProps {
  // Form controls
  control: Control<SchedulingStepFormData>;
  register: UseFormRegister<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  handleFieldBlur: (fieldEvent: React.FocusEvent<HTMLElement>) => void;

  // Save status
  saveToServer: (data: Partial<SchedulingStepFormData>) => void;

  // Roger UserClinic-based data (raw roster; the selects and the suggestion
  // panel derive their own options from this single source).
  rogerClinicians: AvailableClinicianResponse[];
  loadingRogerClinicians: boolean;

  // Whether this intake has been confirmed as an existing patient
  isExistingPatient?: boolean;

  selectedIntake?: { id?: string } | null;

  /** Scroll to a field when the user clicks a validation link. */
  onScrollToField?: (fieldName: string) => void;
}

export const RogerSchedulingStepFormContent: React.FC<
  RogerSchedulingStepFormContentProps
> = ({
  control,
  register,
  setValue,
  errors,
  handleFieldBlur,
  saveToServer,
  rogerClinicians,
  loadingRogerClinicians,
  isExistingPatient = false,
  selectedIntake,
  onScrollToField,
}) => {
  const episodeStartManuallySetRef = useRef(false);

  useEffect(() => {
    episodeStartManuallySetRef.current = false;
  }, [selectedIntake?.id]);

  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const showSmartScheduling = hasFeatureFlag(FeatureFlag.INTAKE_AI_SCHEDULING);

  const episodeStartDate = useWatch({ control, name: 'episode_start_date' });
  const selectedClinicianId = useWatch({ control, name: 'roger_clinician_id' });
  const selectedCaseManagerId = useWatch({
    control,
    name: 'roger_case_manager_id',
  });

  const handleSelectClinician = useCallback(
    (id: string) => {
      setValue('roger_clinician_id', id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      saveToServer({ roger_clinician_id: id });
    },
    [setValue, saveToServer]
  );

  const handleSelectCaseManager = useCallback(
    (id: string) => {
      setValue('roger_case_manager_id', id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      saveToServer({ roger_case_manager_id: id });
    },
    [setValue, saveToServer]
  );

  return (
    <div className='flex flex-col'>
      {/* Validation errors */}
      {!isExistingPatient && Object.keys(errors).length > 0 && (
        <Alert className='mb-3 py-2 px-3 bg-red-50 border-red-100 text-red-900 text-xs'>
          <div className='flex items-start gap-2'>
            <AlertCircle className='h-4 w-4 mt-0.5 flex-shrink-0 text-red-600' />
            <div className='flex-1 text-red-800'>
              <IntakeValidationErrorList
                title='Missing fields required to schedule:'
                ariaLabel='Validation errors'
                items={formErrorsToValidationItems(errors, onScrollToField)}
              />
            </div>
          </div>
        </Alert>
      )}

      {/* Smart scheduling recommendations (new patients only, gated by feature flag) */}
      {!isExistingPatient && showSmartScheduling && (
        <SmartSchedulingSuggestions
          episodeStartDate={episodeStartDate ?? null}
          clinicians={rogerClinicians}
          selectedClinicianId={selectedClinicianId ?? null}
          selectedCaseManagerId={selectedCaseManagerId ?? null}
          loadingRoster={loadingRogerClinicians}
          onSelectClinician={handleSelectClinician}
          onSelectCaseManager={handleSelectCaseManager}
        />
      )}

      <form className='space-y-5' onSubmit={e => e.preventDefault()}>
        {/* Schedule Start of Care */}
        <div className='grid grid-cols-3 gap-3'>
          {/* Row 1: Clinician, Case Manager, SOC Type (hide for existing patients) */}
          {!isExistingPatient && (
            <>
              <ClinicianSelect
                label='Clinician'
                placeholder='Select clinician...'
                clinicians={rogerClinicians}
                role='clinician'
                required={true}
                disabled={loadingRogerClinicians}
                error={errors.roger_clinician_id?.message}
                {...register('roger_clinician_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <ClinicianSelect
                label='Case Manager'
                placeholder='Select case manager...'
                clinicians={rogerClinicians}
                role='caseManager'
                required={true}
                disabled={loadingRogerClinicians}
                error={errors.roger_case_manager_id?.message}
                {...register('roger_case_manager_id', {
                  onBlur: handleFieldBlur,
                })}
              />

              <VirtualizedFormSelect
                label='Type of SOC'
                placeholder='Select SOC type...'
                options={DISCIPLINE_TYPE_OPTIONS}
                required={true}
                disabled={loadingRogerClinicians}
                error={errors.roger_type_of_soc?.message}
                {...register('roger_type_of_soc', {
                  onBlur: handleFieldBlur,
                })}
              />
            </>
          )}
        </div>

        {/* SOC Date & Episode Start Date */}
        <div className='grid grid-cols-3 gap-3'>
          <Controller
            name='soc_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='soc_date'
                label='Start of Care Date'
                value={value}
                htmlFor='rogerSocDate'
                required={true}
                disabled={false}
                error={errors.soc_date?.message}
                onChange={val => {
                  onChange(val);
                  const toSave: Partial<SchedulingStepFormData> = {
                    soc_date: val,
                  };
                  const norm =
                    val && typeof val === 'string' ? val.split('T')[0] : null;
                  if (norm && !episodeStartManuallySetRef.current) {
                    setValue('episode_start_date', val, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    toSave.episode_start_date = val;
                  }
                  saveToServer(toSave);
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />

          <Controller
            name='episode_start_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                fieldAnchor='episode_start_date'
                label='Episode Start Date'
                value={value}
                htmlFor='rogerEpisodeStartDate'
                required={true}
                disabled={false}
                error={errors.episode_start_date?.message}
                onChange={val => {
                  episodeStartManuallySetRef.current = Boolean(val);
                  onChange(val);
                  const toSave: Partial<SchedulingStepFormData> = {
                    episode_start_date: val,
                  };
                  saveToServer(toSave);
                }}
                onBlur={onBlur}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />
        </div>
      </form>
    </div>
  );
};
